import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Button } from '../ui';
import { useCountry } from '../context/CountryContext';
import { createAuditedClient } from '../utils/auditClient';

const client = createAuditedClient('AuditTrailPage');

const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items {
        id
        name
        country
      }
      nextToken
    }
  }
`;

const ListMembers = /* GraphQL */ `
  query ListMembers($CompanyId: String!) {
    listMembers(limit: 1000, filter: { CompanyId: { eq: $CompanyId } }) {
      items {
        id
        sub
        email
      }
      nextToken
    }
  }
`;

const AuditEventsByCompanyByCreatedAt = /* GraphQL */ `
  query AuditEventsByCompanyByCreatedAt(
    $CompanyId: String!
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    auditEventsByCompanyByCreatedAt(
      CompanyId: $CompanyId
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        CompanyId
        actorSub
        actorEmail
        actorGroups
        source
        operation
        entity
        action
        targetId
        summary
        requestPayload
        resultPayload
        metadata
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const ChangeRequestFallback = /* GraphQL */ `
  query ChangeRequestFallback($CompanyId: String!, $limit: Int, $nextToken: String) {
    listChangeRequests(
      limit: $limit
      nextToken: $nextToken
      filter: { CompanyId: { eq: $CompanyId } }
    ) {
      items {
        id
        CompanyId
        entity
        op
        targetId
        payload
        status
        reason
        submittedBySub
        submittedByEmail
        approvedBySub
        approvedAt
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

function parseJson(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function isMissingAuditSchema(error) {
  const msg =
    error?.errors?.[0]?.message ||
    error?.message ||
    '';
  return /auditEventsByCompanyByCreatedAt|AuditEvent|ModelAuditEvent/i.test(String(msg));
}

function formatDateTime(value) {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

function badgeStyle(bg, color = '#fff') {
  return {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: 999,
    background: bg,
    color,
    fontSize: 10,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  };
}

function colorForAction(action) {
  const key = String(action || '').toUpperCase();
  if (key === 'CREATE') return '#16a34a';
  if (key === 'UPDATE') return '#2563eb';
  if (key === 'DELETE') return '#dc2626';
  if (key === 'APPROVE') return '#059669';
  if (key === 'REJECT') return '#b91c1c';
  if (key === 'SUBMIT') return '#4f46e5';
  return '#475569';
}

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function shortId(value, head = 8) {
  const s = String(value || '').trim();
  if (!s) return '--';
  if (s.length <= head + 4) return s;
  return `${s.slice(0, head)}...`;
}

function prettyValue(value) {
  if (value == null || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map((v) => prettyValue(v)).join(', ') : '--';
  if (typeof value === 'object') {
    const text = JSON.stringify(value);
    return text.length > 90 ? `${text.slice(0, 87)}...` : text;
  }
  const str = String(value);
  return str.length > 90 ? `${str.slice(0, 87)}...` : str;
}

function isEmptyValue(value) {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

const FIELD_LABELS = {
  carId: 'Car',
  originalCarId: 'Original car',
  originalCarName: 'Original car name',
  originalCarPlate: 'Original car plate',
  CompanyId: 'Company',
  customerName: 'Customer',
  pickupLocation: 'Pickup location',
  dropoffLocation: 'Dropoff location',
  pickupAt: 'Pickup time',
  dropoffAt: 'Dropoff time',
  start: 'Start',
  end: 'End',
  reservationRef: 'Reservation ref',
  title: 'Title',
  status: 'Status',
  approvalStatus: 'Approval',
  paymentStatus: 'Payment status',
  amountPaid: 'Amount paid',
  rentalAmount: 'Rental amount',
  depositAmount: 'Deposit amount',
  platform: 'Platform',
  notes: 'Notes',
  name: 'Name',
  plate: 'Plate',
  fuelType: 'Fuel',
  transmission: 'Transmission',
  year: 'Year',
  category: 'Category',
  bodyType: 'Body type',
  color: 'Color',
  active: 'Active',
  role: 'Role',
  email: 'Email',
};

function labelForField(key) {
  return FIELD_LABELS[key] || key;
}

function targetLabelFromSnapshot(entity, snapshot, targetId) {
  if (!isPlainObject(snapshot)) return targetId ? `#${shortId(targetId)}` : '--';

  if (entity === 'Reservation') {
    if (snapshot.reservationRef) return `Ref ${snapshot.reservationRef}`;
    if (snapshot.customerName) return snapshot.customerName;
    if (snapshot.title) return snapshot.title;
    return targetId ? `#${shortId(targetId)}` : 'Reservation';
  }
  if (entity === 'Car') {
    if (snapshot.name && snapshot.plate) return `${snapshot.name} (${snapshot.plate})`;
    if (snapshot.name) return snapshot.name;
    if (snapshot.plate) return snapshot.plate;
  }
  if (entity === 'Maintenance') {
    if (snapshot.title) return snapshot.title;
  }
  if (entity === 'Company') {
    if (snapshot.name) return snapshot.name;
  }
  if (entity === 'Member') {
    if (snapshot.email) return snapshot.email;
    if (snapshot.sub) return `User ${shortId(snapshot.sub)}`;
  }
  if (entity === 'AccountingEntry') {
    if (snapshot.description) return snapshot.description;
    if (snapshot.category) return snapshot.category;
  }
  if (entity === 'PriceFrame') {
    if (snapshot.name) return snapshot.name;
  }
  if (entity === 'EmailMessage') {
    if (snapshot.subject) return snapshot.subject;
    if (snapshot.senderEmail) return snapshot.senderEmail;
  }

  return targetId ? `#${shortId(targetId)}` : entity;
}

function deriveBeforeAfter(row) {
  const before = isPlainObject(row.beforeSnapshot) ? row.beforeSnapshot : null;
  let after = isPlainObject(row.afterSnapshot) ? row.afterSnapshot : null;

  const input = isPlainObject(row.requestPayload?.input) ? row.requestPayload.input : null;
  if (!after && input && before) after = { ...before, ...input };
  if (!after && input) after = input;

  return { before, after };
}

function getDiffRows(row) {
  const { before, after } = deriveBeforeAfter(row);
  if (!before && !after) return [];

  const ignored = new Set(['id', 'createdAt', 'updatedAt', '__typename']);
  const hintedKeys = Array.isArray(row.changedKeys) ? row.changedKeys : [];
  const keyPool = hintedKeys.length
    ? hintedKeys
    : Array.from(new Set([...(before ? Object.keys(before) : []), ...(after ? Object.keys(after) : [])]));

  return keyPool
    .filter((key) => !ignored.has(key))
    .map((key) => {
      const beforeValue = before ? before[key] : undefined;
      const afterValue = after ? after[key] : undefined;
      const changed = JSON.stringify(beforeValue ?? null) !== JSON.stringify(afterValue ?? null);
      return {
        key,
        label: labelForField(key),
        before: beforeValue,
        after: afterValue,
        changed,
      };
    })
    .filter((rowDiff) => rowDiff.changed);
}

function buildChangeSentence(diff) {
  const label = diff.label || diff.key;
  const beforeText = prettyValue(diff.before);
  const afterText = prettyValue(diff.after);

  const beforeEmpty = isEmptyValue(diff.before);
  const afterEmpty = isEmptyValue(diff.after);

  if (beforeEmpty && !afterEmpty) return `${label} set to ${afterText}.`;
  if (!beforeEmpty && afterEmpty) return `${label} was cleared (was ${beforeText}).`;
  return `${label} changed from ${beforeText} to ${afterText}.`;
}

async function loadAllAuditEvents(companyId) {
  let nextToken = null;
  const rows = [];

  do {
    const res = await client.graphql({
      query: AuditEventsByCompanyByCreatedAt,
      variables: {
        CompanyId: companyId,
        sortDirection: 'DESC',
        limit: 500,
        nextToken,
      },
      authMode: 'userPool',
    });

    const page = res?.data?.auditEventsByCompanyByCreatedAt;
    rows.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);

  return rows.map((row) => {
    const requestPayload = parseJson(row.requestPayload);
    const resultPayload = parseJson(row.resultPayload);
    const metadata = parseJson(row.metadata);
    const status =
      resultPayload?.status ||
      resultPayload?.approvalStatus ||
      metadata?.status ||
      null;

    return {
      id: `audit-${row.id}`,
      createdAt: row.createdAt || row.updatedAt,
      actorSub: row.actorSub || null,
      actorEmail: row.actorEmail || null,
      source: row.source || null,
      operation: row.operation || null,
      entity: row.entity || 'Unknown',
      action: row.action || 'MUTATION',
      targetId: row.targetId || null,
      summary: row.summary || null,
      status,
      targetLabel: metadata?.targetLabel || null,
      changedKeys: Array.isArray(metadata?.changedKeys) ? metadata.changedKeys : [],
      beforeSnapshot: isPlainObject(metadata?.beforeSnapshot) ? metadata.beforeSnapshot : null,
      afterSnapshot: isPlainObject(metadata?.afterSnapshot) ? metadata.afterSnapshot : null,
      requestPayload,
      resultPayload,
      metadata,
    };
  });
}

async function loadFallbackChangeRequests(companyId) {
  let nextToken = null;
  const rows = [];

  do {
    const res = await client.graphql({
      query: ChangeRequestFallback,
      variables: {
        CompanyId: companyId,
        limit: 500,
        nextToken,
      },
      authMode: 'userPool',
    });

    const page = res?.data?.listChangeRequests;
    rows.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);

  return rows.map((row) => {
    const payload = parseJson(row.payload);
    return {
      id: `cr-${row.id}`,
      createdAt: row.createdAt || row.updatedAt,
      actorSub: row.submittedBySub || null,
      actorEmail: row.submittedByEmail || null,
      source: 'ChangeRequest',
      operation: 'proposeChange',
      entity: row.entity || 'Unknown',
      action: row.op || 'UPDATE',
      targetId: row.targetId || null,
      summary: `${row.entity || 'Change'} ${row.op || 'UPDATE'} request`,
      status: row.status || null,
      targetLabel: null,
      changedKeys: isPlainObject(payload) ? Object.keys(payload) : [],
      beforeSnapshot: null,
      afterSnapshot: null,
      requestPayload: payload,
      resultPayload: {
        status: row.status || null,
        reason: row.reason || null,
        approvedBySub: row.approvedBySub || null,
        approvedAt: row.approvedAt || null,
      },
      metadata: { fallbackMode: true, changeRequestId: row.id },
    };
  });
}

export default function AuditTrailPage() {
  const { selectedCompanyId: ctxCompanyId } = useCountry();

  const [claims, setClaims] = useState(null);
  const [ready, setReady] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [members, setMembers] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('AUDIT');

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [entityFilter, setEntityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [openDetails, setOpenDetails] = useState(new Set());

  const isAdmin = useMemo(() => !!claims?.groups?.includes('admin'), [claims]);
  const activeCompanyId = useMemo(
    () => (isAdmin ? (ctxCompanyId || claims?.CompanyId || null) : (claims?.CompanyId || null)),
    [isAdmin, ctxCompanyId, claims]
  );

  const companyNameMap = useMemo(
    () => new Map(companies.map((co) => [co.id, co.name])),
    [companies]
  );

  const memberBySub = useMemo(() => {
    const map = new Map();
    for (const m of members) {
      if (m?.sub) map.set(m.sub, m.email || m.sub);
    }
    return map;
  }, [members]);

  useEffect(() => {
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const p = tokens?.idToken?.payload || {};
        setClaims({
          CompanyId: p['custom:CompanyId'] ?? p['custom:companyId'] ?? null,
          groups: p['cognito:groups'] || [],
        });
      } catch (e) {
        console.error('AuditTrailPage auth failed', e);
        setClaims({ CompanyId: null, groups: [] });
      } finally {
        setReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!ready || !isAdmin) return;
    (async () => {
      try {
        const res = await client.graphql({
          query: ListCompanies,
          authMode: 'userPool',
        });
        setCompanies((res?.data?.listCompanies?.items || []).filter(Boolean));
      } catch (e) {
        console.error('AuditTrailPage companies load failed', e);
        setCompanies([]);
      }
    })();
  }, [ready, isAdmin]);

  const reload = useCallback(async () => {
    if (!ready || !isAdmin || !activeCompanyId) return;
    setLoading(true);
    setError('');

    try {
      const memRes = await client.graphql({
        query: ListMembers,
        variables: { CompanyId: activeCompanyId },
        authMode: 'userPool',
      });
      setMembers((memRes?.data?.listMembers?.items || []).filter(Boolean));

      let auditRows = [];
      let fallbackRows = [];
      let auditUnavailable = false;

      try {
        auditRows = await loadAllAuditEvents(activeCompanyId);
      } catch (e) {
        if (!isMissingAuditSchema(e)) throw e;
        auditUnavailable = true;
      }

      try {
        fallbackRows = await loadFallbackChangeRequests(activeCompanyId);
      } catch (e) {
        // If audit is also unavailable, then this is a hard failure.
        if (auditUnavailable) throw e;
        console.warn('AuditTrailPage fallback load failed', e);
        fallbackRows = [];
      }

      const merged = [...auditRows, ...fallbackRows];
      merged.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

      const nextMode =
        auditRows.length && fallbackRows.length
          ? 'AUDIT+CHANGE_REQUEST'
          : auditRows.length
            ? 'AUDIT'
            : 'CHANGE_REQUEST_FALLBACK';

      setMode(nextMode);
      setRows(merged);
      setOpenDetails(new Set());
    } catch (e) {
      console.error('AuditTrailPage reload failed', e);
      setRows([]);
      setError(e?.message || 'Failed to load activity.');
    } finally {
      setLoading(false);
    }
  }, [ready, isAdmin, activeCompanyId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const actionOptions = useMemo(
    () => ['ALL', ...Array.from(new Set(rows.map((r) => r.action).filter(Boolean))).sort()],
    [rows]
  );
  const entityOptions = useMemo(
    () => ['ALL', ...Array.from(new Set(rows.map((r) => r.entity).filter(Boolean))).sort()],
    [rows]
  );
  const statusOptions = useMemo(
    () => ['ALL', ...Array.from(new Set(rows.map((r) => r.status).filter(Boolean))).sort()],
    [rows]
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (actionFilter !== 'ALL' && row.action !== actionFilter) return false;
      if (entityFilter !== 'ALL' && row.entity !== entityFilter) return false;
      if (statusFilter !== 'ALL' && (row.status || '') !== statusFilter) return false;

      if (!needle) return true;
      const actor = row.actorEmail || memberBySub.get(row.actorSub) || row.actorSub || '';
      const hay = [
        row.summary,
        row.action,
        row.entity,
        row.source,
        row.targetId,
        row.status,
        actor,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(needle);
    });
  }, [rows, actionFilter, entityFilter, statusFilter, search, memberBySub]);

  if (!ready) return <div style={{ padding: 16 }}>Loading...</div>;
  if (!isAdmin) return <div style={{ padding: 16 }}><b>Not authorized.</b> Audit is admin-only.</div>;
  if (!activeCompanyId) {
    return (
      <div style={{ padding: 16 }}>
        <b>Select an agency.</b> Use the country/agency toggle in the navbar to choose what to audit.
      </div>
    );
  }

  return (
    <div style={{ background: '#f3f4f6', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '14px 12px 24px', display: 'grid', gap: 10 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 22 }}>Audit Trail</h3>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
              {companyNameMap.get(activeCompanyId) || activeCompanyId} - {mode === 'AUDIT' ? 'Live audit events' : mode === 'AUDIT+CHANGE_REQUEST' ? 'Merged from audit + change requests' : 'Fallback from change requests'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => void reload()} style={{ background: 'white' }}>Refresh</Button>
            {loading && <span style={{ fontSize: 12, color: '#6b7280' }}>Loading...</span>}
            <span style={badgeStyle('#e5f7da', '#166534')}>Admin only</span>
          </div>
        </header>

        <section style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', padding: 10, display: 'grid', gap: 8 }}>
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', alignItems: 'center' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search action, entity, actor, target id..."
              style={{ width: '100%', gridColumn: '1 / -1', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, boxSizing: 'border-box' }}
            />
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} style={{ padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }}>
              {actionOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} style={{ padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }}>
              {entityOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }}>
              {statusOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{filtered.length} record(s)</div>
        </section>

        {error && (
          <section style={{ border: '1px solid #fecaca', borderRadius: 12, background: '#fff1f2', color: '#991b1b', padding: 12, fontSize: 13 }}>
            {error}
          </section>
        )}

        <section style={{ display: 'grid', gap: 8 }}>
          {filtered.length === 0 && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 14, color: '#6b7280' }}>
              No audit records for this selection. Current agency: {companyNameMap.get(activeCompanyId) || activeCompanyId}.
            </div>
          )}

          {filtered.map((row) => {
            const actor = row.actorEmail || memberBySub.get(row.actorSub) || row.actorSub || 'Unknown';
            const isOpen = openDetails.has(row.id);
            const targetLabel =
              row.targetLabel ||
              targetLabelFromSnapshot(row.entity, row.afterSnapshot || row.beforeSnapshot, row.targetId);
            const diffRows = getDiffRows(row);
            const changeSentences = diffRows.map(buildChangeSentence);
            const sentencePreview = changeSentences.slice(0, 2).join(' ');
            const hiddenCount = Math.max(0, changeSentences.length - 2);

            return (
              <article key={row.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: 8 }}>
                  <div style={{ minWidth: 0, display: 'grid', gap: 4 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                      <span style={badgeStyle(colorForAction(row.action))}>{row.action || 'MUTATION'}</span>
                      <span style={badgeStyle('#334155')}>{row.entity || 'Unknown'}</span>
                      {row.status ? <span style={badgeStyle('#0f766e')}>{row.status}</span> : null}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#111827',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: '1 1 260px',
                        }}
                        title={row.summary || `${row.action || 'MUTATION'} ${row.entity || 'Unknown'}`}
                      >
                        {row.summary || `${row.action || 'MUTATION'} ${row.entity || 'Unknown'}`}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#4b5563',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={`Actor: ${actor}${targetLabel ? ` | Target: ${targetLabel}` : ''}${row.source ? ` | Source: ${row.source}` : ''}`}
                    >
                      Actor: {actor}
                      {targetLabel ? ` | Target: ${targetLabel}` : ''}
                      {row.source ? ` | Source: ${row.source}` : ''}
                    </div>
                    {diffRows.length > 0 && (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#0f172a',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          background: '#eef4ff',
                          border: '1px solid #c7ddff',
                          borderRadius: 6,
                          padding: '3px 6px',
                        }}
                        title={changeSentences.join('\n')}
                      >
                        {sentencePreview}{hiddenCount > 0 ? ` (+${hiddenCount} more)` : ''}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'grid', justifyItems: 'end', gap: 4 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDateTime(row.createdAt)}</div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = new Set(openDetails);
                        if (next.has(row.id)) next.delete(row.id);
                        else next.add(row.id);
                        setOpenDetails(next);
                      }}
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: 7,
                        background: '#fff',
                        color: '#374151',
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '3px 8px',
                        cursor: 'pointer',
                      }}
                    >
                      {isOpen ? 'Hide' : 'Details'}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid #e5e7eb', padding: '8px 10px 10px', display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ marginBottom: 3, fontSize: 11, color: '#374151', fontWeight: 700 }}>Before / After</div>
                      {diffRows.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                          Field-level diff unavailable for this older log entry.
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: 8, padding: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1e3a8a', marginBottom: 4 }}>What changed</div>
                            <div style={{ display: 'grid', gap: 3 }}>
                              {changeSentences.slice(0, 8).map((sentence, idx) => (
                                <div key={`${row.id}-sentence-${idx}`} style={{ fontSize: 11, color: '#1e3a8a' }}>
                                  {idx + 1}. {sentence}
                                </div>
                              ))}
                              {changeSentences.length > 8 && (
                                <div style={{ fontSize: 11, color: '#1e3a8a', opacity: 0.85 }}>
                                  +{changeSentences.length - 8} more changes
                                </div>
                              )}
                            </div>
                          </div>

                          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '140px 1fr 1fr',
                              gap: 0,
                              fontSize: 11,
                              fontWeight: 700,
                              background: '#f8fafc',
                              color: '#334155',
                              borderBottom: '1px solid #e5e7eb',
                            }}
                          >
                            <div style={{ padding: '6px 8px' }}>Field</div>
                            <div style={{ padding: '6px 8px' }}>Before</div>
                            <div style={{ padding: '6px 8px' }}>After</div>
                          </div>
                          {diffRows.map((diff) => (
                            <div
                              key={diff.key}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '140px 1fr 1fr',
                                gap: 0,
                                fontSize: 11,
                                borderBottom: '1px solid #f1f5f9',
                                background: '#fafcff',
                              }}
                            >
                              <div style={{ padding: '6px 8px', color: '#334155', fontWeight: 700 }}>{diff.label}</div>
                              <div
                                style={{
                                  padding: '6px 8px',
                                  color: '#9f1239',
                                  background: '#fff1f2',
                                  borderLeft: '1px solid #ffe4e6',
                                }}
                                title={`Before: ${prettyValue(diff.before)}`}
                              >
                                {prettyValue(diff.before)}
                              </div>
                              <div
                                style={{
                                  padding: '6px 8px',
                                  color: '#166534',
                                  fontWeight: 700,
                                  background: '#ecfdf3',
                                  borderLeft: '1px solid #dcfce7',
                                }}
                                title={`After: ${prettyValue(diff.after)}`}
                              >
                                {prettyValue(diff.after)}
                              </div>
                            </div>
                          ))}
                        </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ marginBottom: 3, fontSize: 11, color: '#374151', fontWeight: 700 }}>Request</div>
                      <pre style={{ margin: 0, background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: 8, overflow: 'auto', maxHeight: 220, fontSize: 11, lineHeight: 1.35 }}>
                        {JSON.stringify(row.requestPayload ?? null, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div style={{ marginBottom: 3, fontSize: 11, color: '#374151', fontWeight: 700 }}>Result</div>
                      <pre style={{ margin: 0, background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: 8, overflow: 'auto', maxHeight: 220, fontSize: 11, lineHeight: 1.35 }}>
                        {JSON.stringify(row.resultPayload ?? null, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div style={{ marginBottom: 3, fontSize: 11, color: '#374151', fontWeight: 700 }}>Metadata</div>
                      <pre style={{ margin: 0, background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: 8, overflow: 'auto', maxHeight: 220, fontSize: 11, lineHeight: 1.35 }}>
                        {JSON.stringify(row.metadata ?? null, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
}
